<template>
  <b-modal :id="modalId" size="xl" modal-class="modal-manage-view">
    <div class="wrapper">
      <div class="fuel-consumption-trend__heading">
        <h5 class="fuel-consumption-trend__title">
          {{ $t('dashboard.title.fuelConsumptionDailyTrend') }}
        </h5>
        <span class="fuel-consumption-trend__subtitle">{{ getFilterLabel }}</span>
      </div>
      <div class="fuel-consumption-trend__thresholds mb-3">
        <div v-for="sum in getAvg" :key="`v-${sum.key}`" class="fuel-consumption-trend__threshold">
          <div class="fuel-consumption-trend__threshold-heading">
            <i :class="`mc-fa ${sum.icon}`" :style="{ color: sum.iconColor }"></i>
            <span>{{ $t(sum.title) }}</span>
          </div>
          <div class="fuel-consumption-trend__threshold-control">
            <span class="fuel-consumption-trend__threshold-label">
              {{ $t('dashboard.label.avg') }}
            </span>
            <span class="fuel-consumption-trend__threshold-value">
              {{ sum.value }} {{ sum.format }}
            </span>
          </div>
        </div>
      </div>
      <div v-if="!dataChart.bar.isLoading" class="fuel-consumption-trend__vehicle-chart">
        <div v-if="hasVehicleChartData" class="fuel-consumption-trend__chart-viewport">
          <b-row class="fuel-consumption-trend__chart-row">
            <b-col v-for="(chart, index) in vehicleBarCharts" :key="chart.key" md="2">
              <div class="fuel-consumption-trend__chart" :style="barChartStyle">
                <HorizontalBar
                  :key="fuelSummaryTrend[index].key"
                  :labels="dataChart.bar.labels ?? []"
                  :datasets="chart.datasets ?? []"
                  :options="chart.options"
                  :height="barChartHeight"
                  :styles="barChartStyle"
                  :bar-label="true"
                  :unit-label="chart.datasets[0].unit"
                />
              </div>
            </b-col>
          </b-row>
        </div>
        <div v-else class="fuel-consumption-trend__empty">
          {{ $t('global.text.dataNotFound') }}
        </div>
      </div>
      <b-skeleton v-else width="100%" height="380px" />
      <span class="text-desc">{{ $t('dashboard.text.redirectToTripHistory') }}</span>
    </div>
    <template #modal-header>
      <span class="__title">
        {{ $t('dashboard.title.fuelConsumptionTrend') }} {{ licensePlate }}
      </span>
    </template>
    <template #modal-footer>
      <b-row class="modal__footer mx-0">
        <b-col sm="12" md="12" class="modal__footer-btn">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.close')"
            @click="closeFilter"
          />
        </b-col>
      </b-row>
    </template>
  </b-modal>
</template>
<script>
import { QueryCache } from '@/mixin/query-cache'
import { Helper } from '@/mixin/helper'
import HorizontalBar from '@/components/Chart/HorizontalBar.vue'
import moment from 'moment'
import { SUMMARY_FUEL_DASHBOARD } from '@/services/api/dashboard/dashboard'
import McButton from '@/components/McButton'
import { mapGetters } from 'vuex'

moment.locale('id')

const BAR_CHART_MIN_HEIGHT = 370
const BAR_CHART_ROW_HEIGHT = 28
const BAR_CHART_VERTICAL_PADDING = 20

export default {
  props: {
    modalId: {
      type: String,
      required: true,
    },
  },
  components: {
    HorizontalBar,
    McButton,
  },
  mixins: [Helper],
  data() {
    return {
      filterLabel: '',
      licensePlate: '',
      filterType: 'date',
      fuelDashboardData: [],
      queryCache: new QueryCache(),
      dataChart: {
        bar: {
          isLoading: false,
          labels: [],
          datasetFuel: [
            {
              label: this.$t('dashboard.label.fuelConsumption'),
              type: 'bar',
              data: [],
              fill: true,
              spanGaps: false,
              unit: 'L',
              backgroundColor: '#77ACE9',
              borderRadius: 50,
              borderSkipped: 'bottom',
              barThickness: 16,
              maxBarThickness: 18,
            },
            {
              label: this.$t('dashboard.label.fuelConsumption'),
              type: 'line',
              data: [],
              borderColor: '#F87766',
              fill: false,
              pointRadius: 2,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: false,
            },
          ],
          datasetEfficiency: [
            {
              label: this.$t('dashboard.label.efficiency'),
              type: 'bar',
              data: [],
              fill: true,
              spanGaps: false,
              unit: 'Km/L',
              backgroundColor: '#137C55',
              borderRadius: 50,
              borderSkipped: 'bottom',
              barThickness: 16,
              maxBarThickness: 18,
            },
            {
              label: this.$t('dashboard.label.efficiency'),
              type: 'line',
              data: [],
              borderColor: '#F87766',
              fill: false,
              pointRadius: 2,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: false,
            },
          ],
          datasetTrip: [
            {
              label: this.$t('dashboard.label.trip'),
              type: 'bar',
              data: [],
              fill: true,
              spanGaps: false,
              unit: 'Km',
              backgroundColor: '#1C74DA',
              borderRadius: 50,
              borderSkipped: 'bottom',
              barThickness: 16,
              maxBarThickness: 18,
            },
            {
              label: this.$t('dashboard.label.trip'),
              type: 'line',
              data: [],
              borderColor: '#F87766',
              fill: false,
              pointRadius: 2,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: false,
            },
          ],
          datasetDriverAlert: [
            {
              label: this.$t('dashboard.label.driverAlert'),
              type: 'bar',
              data: [],
              fill: true,
              spanGaps: false,
              unit: '',
              backgroundColor: '#F41D00',
              borderRadius: 50,
              borderSkipped: 'bottom',
              barThickness: 16,
              maxBarThickness: 18,
            },
            {
              label: this.$t('dashboard.label.driverAlert'),
              type: 'line',
              data: [],
              borderColor: '#F87766',
              fill: false,
              pointRadius: 2,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: false,
            },
          ],
          datasetIdleDuration: [
            {
              label: this.$t('dashboard.label.idleDuration'),
              type: 'bar',
              data: [],
              fill: true,
              spanGaps: false,
              unit: 'min',
              backgroundColor: '#FF8A00',
              borderRadius: 50,
              borderSkipped: 'bottom',
              barThickness: 16,
              maxBarThickness: 18,
            },
            {
              label: this.$t('dashboard.label.idleDuration'),
              type: 'line',
              data: [],
              borderColor: '#F87766',
              fill: false,
              pointRadius: 2,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: false,
            },
          ],
          datasetTripDuration: [
            {
              label: this.$t('dashboard.label.tripDuration'),
              type: 'bar',
              data: [],
              fill: true,
              spanGaps: false,
              unit: 'min',
              backgroundColor: '#CC1D92',
              borderRadius: 50,
              borderSkipped: 'bottom',
              barThickness: 16,
              maxBarThickness: 18,
            },
            {
              label: this.$t('dashboard.label.tripDuration'),
              type: 'line',
              data: [],
              borderColor: '#F87766',
              fill: false,
              pointRadius: 2,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: false,
            },
          ],
        },
        vehicleId: [],
        period: [],
      },
      fuelSummaryTrend: [
        {
          key: 'avgFuelConsumption',
          title: 'dashboard.label.fuelConsumption',
          icon: 'icon-outline_fuel',
          value: 0,
          format: 'L',
          iconColor: '#77ACE9',
          key: 0,
          formatting: (value) => {
            return `${value} L`
          },
        },
        {
          key: 'avgEfficiency',
          title: 'dashboard.label.efficiency',
          icon: 'icon-fill_droplet',
          value: 0,
          format: 'Km/L',
          iconColor: '#137C55',
          key: 10,
          formatting: (value) => {
            return `${value} Km/L`
          },
        },
        {
          key: 'avgTrip',
          title: 'dashboard.label.trip',
          icon: 'icon-outline_calculate',
          value: 0,
          format: 'Km',
          iconColor: '#1C74DA',
          key: 20,
          formatting: (value) => {
            return `${value} Km`
          },
        },
        {
          key: 'avgAlert',
          title: 'dashboard.label.driverAlert',
          icon: 'icon-fill_alert-triangle',
          value: 0,
          format: this.$t('dashboard.label.incident'),
          iconColor: '#F41D00',
          key: 30,
          formatting: (value) => {
            return `${value}`
          },
        },
        {
          key: 'avgIdleDuration',
          title: 'dashboard.label.idleDuration',
          icon: 'icon-outline_truck-idle',
          value: 0,
          format: 'min',
          iconColor: '#FF8A00',
          key: 40,
          formatting: (value) => {
            return this.convertTimeToString(value)
          },
        },
        {
          key: 'avgTripDuration',
          title: 'dashboard.label.tripDuration',
          icon: 'icon-fill_timer',
          value: 0,
          format: 'min',
          iconColor: '#CC1D92',
          key: 50,
          formatting: (value) => {
            return this.convertTimeToString(value)
          },
        },
      ],
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    barChartOptions() {
      return {
        onClick: (event, activeElements) => {
          if (activeElements.length <= 0) return
          const index = activeElements[0]._index
          this.redirectToLiveview(index)
        },
        legend: {
          display: false,
        },
        layout: {
          padding: {
            top: 2,
            right: 22,
            bottom: 0,
            left: 0,
          },
        },
        plugins: {
          datalabels: {
            align: 'right',
            anchor: 'end',
            clamp: true,
            color: '#516078',
            font: {
              size: 11,
              weight: 'normal',
            },
          },
        },
        scales: {
          xAxes: [
            {
              afterDataLimits(scale) {
                if (scale.max > 0) {
                  scale.max *= 1.5
                }
              },
              ticks: {
                beginAtZero: true,
                display: false,
              },
              gridLines: {
                display: false,
                drawBorder: false,
              },
            },
          ],
          yAxes: [
            {
              gridLines: {
                borderDash: [5, 5],
                color: '#e3e8f0',
                drawBorder: false,
                zeroLineColor: '#e3e8f0',
              },
              ticks: {
                fontColor: '#4f5664',
                fontSize: 11,
                padding: 4,
              },
            },
          ],
        },
      }
    },
    barChartOptionsWithoutYLabel() {
      return {
        onClick: (event, activeElements) => {
          if (activeElements.length <= 0) return
          const index = activeElements[0]._index
          this.redirectToLiveview(index)
        },
        legend: {
          display: false,
        },
        layout: {
          padding: {
            top: 2,
            right: 22,
            bottom: 0,
            left: 0,
          },
        },
        plugins: {
          datalabels: {
            align: 'right',
            anchor: 'end',
            clamp: true,
            color: '#516078',
            font: {
              size: 11,
              weight: 'normal',
            },
          },
        },
        scales: {
          xAxes: [
            {
              afterDataLimits(scale) {
                if (scale.max > 0) {
                  scale.max *= 1.5
                }
              },
              ticks: {
                beginAtZero: true,
                display: false,
              },
              gridLines: {
                display: false,
                drawBorder: false,
              },
            },
          ],
          yAxes: [
            {
              gridLines: {
                borderDash: [5, 5],
                color: '#e3e8f0',
                drawBorder: false,
                zeroLineColor: '#e3e8f0',
              },
              ticks: {
                display: false,
              },
            },
          ],
        },
      }
    },
    getFilterLabel() {
      return this.filterLabel
    },
    getAvg() {
      return this.fuelSummaryTrend
    },
    barChartHeight() {
      const vehicleCount = this.dataChart.bar.labels.length
      const contentHeight = vehicleCount * BAR_CHART_ROW_HEIGHT + BAR_CHART_VERTICAL_PADDING

      return Math.max(BAR_CHART_MIN_HEIGHT, contentHeight)
    },
    barChartStyle() {
      return {
        position: 'relative',
        height: `${this.barChartHeight}px`,
      }
    },
    hasVehicleChartData() {
      return this.dataChart.bar.labels.length > 0
    },
    vehicleBarCharts() {
      return [
        {
          key: 'fuel',
          datasets: this.dataChart.bar.datasetFuel,
          options: this.barChartOptions,
        },
        {
          key: 'efficiency',
          datasets: this.dataChart.bar.datasetEfficiency,
          options: this.barChartOptionsWithoutYLabel,
        },
        {
          key: 'trip',
          datasets: this.dataChart.bar.datasetTrip,
          options: this.barChartOptionsWithoutYLabel,
        },
        {
          key: 'driver-alert',
          datasets: this.dataChart.bar.datasetDriverAlert,
          options: this.barChartOptionsWithoutYLabel,
        },
        {
          key: 'idle-duration',
          datasets: this.dataChart.bar.datasetIdleDuration,
          options: this.barChartOptionsWithoutYLabel,
        },
        {
          key: 'trip-duration',
          datasets: this.dataChart.bar.datasetTripDuration,
          options: this.barChartOptionsWithoutYLabel,
        },
      ]
    },
  },
  methods: {
    closeFilter() {
      this.$bvModal.hide(this.modalId)
    },
    redirectToLiveview(index) {
      const date = this.fuelDashboardData[index].date
      if (!date) return
      const finalDate = moment(date).format('YYYY-MM-DD')
      const startDate = moment(finalDate + ' 00:00:00.000').toISOString()
      const endDate = moment(finalDate + ' 23:59:59.000').toISOString()
      const link = `liveview-2/?type=history&vehicleId=${this.vehicleId[0]}&startDate=${startDate}&endDate=${endDate}`
      window.open(link, '_blank')
    },
    loadFilter(filters) {
      this.vehicleId = filters.vehicleId
      this.period = [filters.startDate, filters.endDate]
      this.filterLabel = filters.filterLabel
      this.licensePlate = filters.licensePlate
      this.loadData({
        vehicleId: this.vehicleId,
        startDate: moment(this.period[0] + ' 00:00:00.000').toISOString(),
        endDate: moment(this.period[1] + ' 23:59:59.000').toISOString(),
      })
    },
    async loadData(filters) {
      let filter = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        groupBy: 'date',
        vehicleId: filters.vehicleId,
        disabledOdoFuel: this.hasAccess(['b1x-new-fuel-expectation:read']),
      }

      this.dataChart.bar.isLoading = true

      try {
        const result = await this.queryCache.fetch(
          SUMMARY_FUEL_DASHBOARD,
          {
            filter,
          },
          ''
        )
        const fuel = result.summaryFuelDashboard.summaryFuelDashboard.data || []
        this.fuelDashboardData = fuel

        this.mapFuelDashboardDateGrouping(this.fuelDashboardData)
        this.mapAverageFuelData(this.fuelDashboardData)
      } finally {
        this.dataChart.bar.isLoading = false
      }
    },
    mapFuelDashboardDateGrouping(fuel) {
      this.dataChart.bar.labels = fuel.map((item) => moment(item.date).format('DD MMM YYYY') || '-')
      this.dataChart.bar.datasetFuel[0].data = fuel.map((item) =>
        this.toChartNumber(item.fuelConsumption)
      )
      this.dataChart.bar.datasetFuel[1].data = this.emptyThresholdData(fuel)
      this.dataChart.bar.datasetEfficiency[0].data = fuel.map((item) =>
        this.toChartNumber(item.fuelEfficiency)
      )
      this.dataChart.bar.datasetEfficiency[1].data = this.emptyThresholdData(fuel)
      this.dataChart.bar.datasetTrip[0].data = fuel.map((item) =>
        this.toChartNumber(item.distanceInKm)
      )
      this.dataChart.bar.datasetTrip[1].data = this.emptyThresholdData(fuel)
      this.dataChart.bar.datasetDriverAlert[0].data = fuel.map((item) =>
        this.toChartNumber(item.driverAlert)
      )
      this.dataChart.bar.datasetDriverAlert[1].data = this.emptyThresholdData(fuel)
      this.dataChart.bar.datasetIdleDuration[0].data = fuel.map((item) =>
        this.toChartNumber((item.idleDurationInSecond / 60).toFixed(0))
      )
      this.dataChart.bar.datasetIdleDuration[1].data = this.emptyThresholdData(fuel)
      this.dataChart.bar.datasetTripDuration[0].data = fuel.map((item) =>
        this.toChartNumber((item.tripDurationInSecond / 60).toFixed(0))
      )
      this.dataChart.bar.datasetTripDuration[1].data = this.emptyThresholdData(fuel)
    },
    mapAverageFuelData(fuel) {
      const totalData = {
        avgFuelConsumption: 0,
        avgEfficiency: 0,
        avgTrip: 0,
        avgAlert: 0,
        avgIdleDuration: 0,
        avgTripDuration: 0,
      }
      for (const f of fuel) {
        totalData.avgFuelConsumption += f.fuelConsumption
        totalData.avgTrip += f.distanceInKm
        totalData.avgAlert += f.driverAlert
        totalData.avgIdleDuration += f.idleDurationInSecond
        totalData.avgTripDuration += f.tripDurationInSecond
        totalData.avgEfficiency += f.fuelEfficiency
      }
      totalData.avgFuelConsumption = (totalData.avgFuelConsumption / fuel.length).toFixed(2)
      totalData.avgTrip = (totalData.avgTrip / fuel.length).toFixed(2)
      totalData.avgAlert = (totalData.avgAlert / fuel.length).toFixed(0)
      totalData.avgIdleDuration = Number((totalData.avgIdleDuration / fuel.length).toFixed(0))
      totalData.avgTripDuration = Number((totalData.avgTripDuration / fuel.length).toFixed(0))
      totalData.avgEfficiency = totalData.avgFuelConsumption
        ? (totalData.avgTrip / totalData.avgFuelConsumption).toFixed(2)
        : null
      this.fuelSummaryTrend[0].value = totalData.avgFuelConsumption
      this.fuelSummaryTrend[1].value = totalData.avgEfficiency
      this.fuelSummaryTrend[2].value = totalData.avgTrip
      this.fuelSummaryTrend[3].value = totalData.avgAlert
      this.fuelSummaryTrend[4].value = (totalData.avgIdleDuration / 60).toFixed(0)
      this.fuelSummaryTrend[5].value = (totalData.avgTripDuration / 60).toFixed(0)
      this.setAvgVehicleChart()
    },
    setAvgVehicleChart() {
      this.dataChart.bar.datasetFuel[1].data = this.fuelDashboardData.map((item) =>
        Number(this.fuelSummaryTrend[0].value)
      )
      this.dataChart.bar.datasetEfficiency[1].data = this.fuelDashboardData.map((item) =>
        Number(this.fuelSummaryTrend[1].value)
      )
      this.dataChart.bar.datasetTrip[1].data = this.fuelDashboardData.map((item) =>
        Number(this.fuelSummaryTrend[2].value)
      )
      this.dataChart.bar.datasetDriverAlert[1].data = this.fuelDashboardData.map((item) =>
        Number(this.fuelSummaryTrend[3].value)
      )
      this.dataChart.bar.datasetIdleDuration[1].data = this.fuelDashboardData.map((item) =>
        Number(this.fuelSummaryTrend[4].value)
      )
      this.dataChart.bar.datasetTripDuration[1].data = this.fuelDashboardData.map((item) =>
        Number(this.fuelSummaryTrend[5].value)
      )
    },
    toChartNumber(value) {
      const numberValue = Number(value)
      return Number.isNaN(numberValue) ? 0 : numberValue
    },
    emptyThresholdData(fuel) {
      return fuel.map(() => 0)
    },
  },
}
</script>

<style scoped>
.text-desc {
  padding-top: 16px;
  display: block;
}

.wrapper {
  background: #ffffff;
}

.fuel-consumption-trend__heading {
  min-width: 0;
}

.fuel-consumption-trend__title {
  margin-bottom: 2px;
  color: #253858;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.fuel-consumption-trend__subtitle {
  display: block;
  color: #253858;
  font-size: 11px;
  line-height: 16px;
}

.fuel-consumption-trend__thresholds {
  display: grid;
  grid-template-columns: repeat(6, minmax(128px, 1fr));
  gap: 12px 20px;
  margin-top: 16px;
}

.fuel-consumption-trend__threshold {
  min-width: 0;
}

.fuel-consumption-trend__threshold-heading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 18px;
  color: #667085;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
}

.fuel-consumption-trend__threshold-heading i {
  flex: 0 0 auto;
  margin-right: 4px;
  font-size: 14px;
}

.fuel-consumption-trend__threshold-control {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 4px;
}

.fuel-consumption-trend__threshold-label {
  margin-right: 6px;
  color: #667085;
  font-size: 12px;
  line-height: 24px;
}

.fuel-consumption-trend__threshold-value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  padding: 3px 10px;
  color: #0057b8;
  background: #e8f2ff;
  border-radius: 4px;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  white-space: nowrap;
}

.fuel-consumption-trend__vehicle-chart {
  margin-top: 16px;
}

.fuel-consumption-trend__chart-viewport {
  max-height: 380px;
  overflow: auto;
  border: 1px solid #edf1f6;
  border-radius: 2px;
  background: #ffffff;
}

.fuel-consumption-trend__chart-row {
  flex-wrap: nowrap;
  width: 100%;
  min-height: 0;
  margin-right: 0;
  margin-left: 0;
  padding-left: 10px;
}

.fuel-consumption-trend__chart-row > [class*='col'] {
  flex: 0 0 16.6667%;
  max-width: 16.6667%;
  min-height: 0;
  padding-right: 0;
  padding-left: 0;
}

.fuel-consumption-trend__chart-row > [class*='col'] + [class*='col'] {
  border-left: 1px solid #edf1f6;
}

.fuel-consumption-trend__chart {
  position: relative;
  overflow: hidden;
  background: #ffffff;
}

.fuel-consumption-trend__chart ::v-deep canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

.fuel-consumption-trend__empty {
  display: flex;
  min-height: 160px;
  align-items: center;
  justify-content: center;
  color: #667085;
  border: 1px solid #edf1f6;
  border-radius: 2px;
  background: #ffffff;
  font-size: 13px;
}

.__title {
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.modal__footer {
  width: 100%;
}

.modal__footer-btn {
  display: flex;
  justify-content: flex-end;
  padding-right: 0;
  padding-left: 0;
}

@media (max-width: 991.98px) {
  .fuel-consumption-trend__thresholds {
    grid-template-columns: repeat(2, minmax(128px, 1fr));
  }
}

@media (max-width: 575.98px) {
  .wrapper {
    padding: 12px !important;
  }

  .fuel-consumption-trend__thresholds {
    grid-template-columns: 1fr;
  }

  .fuel-consumption-trend__threshold-heading,
  .fuel-consumption-trend__threshold-control {
    justify-content: flex-start;
  }
}
</style>
